import moment from 'moment-timezone';
import { Box } from '@mui/material';
import { CampaignFlight } from '../types/Campaign';

export interface FlightStatusResult {
    status: 'Live' | 'Upcoming' | 'Completed';
    color: string;
    backgroundColor: string;
}

export interface FlightSummary {
    totalBudgetAmount: number;
    totalDspBudgetAmount: number;
    totalCost: number;
    totalRemaining: number;
    earliestStartDate: Date | null;
    latestEndDate: Date | null;
}

/**
 * Determines the status of a flight based on its start and end dates
 * Uses exact date comparison (same logic as isFlightLive/isFlightUpcoming in CampaignSetupDeliveryCard)
 */
export const getFlightStatus = (startDateTime: Date, endDateTime: Date): FlightStatusResult => {
    const now = moment.tz('America/New_York');
    const start = moment.tz(new Date(startDateTime), 'America/New_York');
    const end = moment.tz(new Date(endDateTime), 'America/New_York');

    if (start.isAfter(now)) {
        return {
            status: 'Upcoming',
            color: '#ffffff',
            backgroundColor: '#056def',
        };
    } else if (end.isBefore(now)) {
        return {
            status: 'Completed',
            color: 'rgb(7,7,7)',
            backgroundColor: '#aaa9a9',
        };
    } else {
        return {
            status: 'Live',
            color: '#FFFFFF',
            backgroundColor: '#2E7D32',
        };
    }
};

/**
 * Renders a status badge component
 */
export const getFlightStatusBadge = (startDateTime: Date, endDateTime: Date) => {
    const { status, color, backgroundColor } = getFlightStatus(startDateTime, endDateTime);

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                px: 1,
                py: 0.5,
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: backgroundColor,
                color: color,
            }}
        >
            {status}
        </Box>
    );
};

/**
 * Calculates aggregate summary data for multiple flights
 */
export const calculateFlightSummary = (flights: CampaignFlight[]): FlightSummary => {
    if (!flights || flights.length === 0) {
        return {
            totalBudgetAmount: 0,
            totalDspBudgetAmount: 0,
            totalCost: 0,
            totalRemaining: 0,
            earliestStartDate: null,
            latestEndDate: null,
        };
    }

    return {
        totalBudgetAmount: flights.reduce((sum, row) => sum + row.budgetAmount, 0),
        totalDspBudgetAmount: flights.reduce((sum, row) => sum + row.dspBudget, 0),
        totalCost: flights.reduce((sum, row) => sum + row.totalCost, 0),
        totalRemaining: flights.reduce((sum, row) => sum + (row.budgetAmount - row.totalCost), 0),
        earliestStartDate:
            flights.map((f) => f.startDateTime).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ??
            null,
        latestEndDate:
            flights.map((f) => f.endDateTime).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null,
    };
};
